import { useEffect, useState } from "react";
import {
  Users, HeartHandshake, Briefcase, Wallet, TrendingUp, TrendingDown,
  Sparkles, CalendarDays, Handshake, MapPin,
} from "lucide-react";
import { api, eur, frDate } from "../lib/api";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Legend, PieChart, Pie, Cell,
} from "recharts";

const colors = ["#1E3A8A", "#EA580C", "#16A34A", "#F59E0B", "#3B82F6", "#9333EA"];

function KpiCard({ icon: Icon, label, value, accent = "navy", testid }) {
  const bg = { navy: "bg-brand-navy/5 text-brand-navy", orange: "bg-brand-orange/10 text-brand-orange", green: "bg-brand-green/10 text-brand-green" }[accent];
  return (
    <div data-testid={testid} className="bg-white border border-brand-border rounded-xl p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <div className="small-caps">{label}</div>
          <div className="mt-2 font-serif text-3xl text-brand-navy">{value}</div>
        </div>
        <div className={`p-2.5 rounded-lg ${bg}`}>
          <Icon size={20} strokeWidth={1.6} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setD(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-brand-orange">
            <Sparkles size={14} />
            <span className="small-caps">Vue d'ensemble</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-brand-navy tracking-tight mt-1">
            Tableau de bord
          </h1>
          <p className="text-gray-500 mt-1">
            L'essentiel de l'activité de l'association, en un coup d'œil.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-solde" icon={Wallet} label="Solde actuel" value={eur(d?.solde)} accent="navy" />
        <KpiCard testid="kpi-entrees" icon={TrendingUp} label="Total entrées" value={eur(d?.total_entrees)} accent="green" />
        <KpiCard testid="kpi-sorties" icon={TrendingDown} label="Total sorties" value={eur(d?.total_sorties)} accent="orange" />
        <KpiCard testid="kpi-members" icon={Users} label="Membres" value={d?.members_count ?? 0} accent="navy" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-volunteers" icon={HeartHandshake} label="Bénévoles" value={d?.volunteers_count ?? 0} accent="green" />
        <KpiCard testid="kpi-employees" icon={Briefcase} label="Salarié(e)s" value={d?.employees_count ?? 0} accent="orange" />
        <KpiCard testid="kpi-partners" icon={Handshake} label="Partenaires" value={d?.partners_count ?? 0} accent="navy" />
        <KpiCard testid="kpi-upcoming-events" icon={CalendarDays} label="Événements à venir" value={d?.upcoming_events_count ?? 0} accent="orange" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-brand-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="small-caps">Derniers 6 mois</div>
              <h3 className="font-serif text-2xl text-brand-navy">Entrées vs sorties</h3>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d?.monthly_series || []}>
                <defs>
                  <linearGradient id="gEntree" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16A34A" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#16A34A" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gSortie" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EA580C" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#EA580C" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#F3F4F6" />
                <XAxis dataKey="mois" tick={{ fontSize: 12, fill: "#6B7280" }} />
                <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} />
                <Tooltip
                  formatter={(v) => eur(v)}
                  contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }}
                />
                <Area type="monotone" dataKey="entrees" name="Entrées" stroke="#16A34A" fill="url(#gEntree)" strokeWidth={2} />
                <Area type="monotone" dataKey="sorties" name="Sorties" stroke="#EA580C" fill="url(#gSortie)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-brand-border rounded-xl p-6">
          <div className="small-caps">Répartition</div>
          <h3 className="font-serif text-2xl text-brand-navy mb-4">Dépenses par catégorie</h3>
          {(d?.expense_categories || []).length === 0 ? (
            <div className="text-sm text-gray-400 py-10 text-center">
              Aucune dépense enregistrée pour l'instant.
            </div>
          ) : (
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.expense_categories} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3}>
                    {d.expense_categories.map((_, i) => (
                      <Cell key={i} fill={colors[i % colors.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => eur(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-brand-border rounded-xl p-6">
        <div className="small-caps">Solde mensuel</div>
        <h3 className="font-serif text-2xl text-brand-navy mb-4">Évolution du solde</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d?.monthly_series || []}>
              <CartesianGrid vertical={false} stroke="#F3F4F6" />
              <XAxis dataKey="mois" tick={{ fontSize: 12, fill: "#6B7280" }} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} />
              <Tooltip formatter={(v) => eur(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
              <Bar dataKey="solde" name="Solde" fill="#1E3A8A" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-brand-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="small-caps">Agenda</div>
            <h3 className="font-serif text-2xl text-brand-navy">Prochains événements</h3>
          </div>
          <a href="/app/evenements" className="text-sm text-brand-orange hover:underline">Voir tout →</a>
        </div>
        {(d?.upcoming_events || []).length === 0 ? (
          <div className="text-sm text-gray-400 py-6 text-center border border-dashed border-brand-border rounded-lg">
            Aucun événement planifié. Créez votre premier événement depuis la section Événements.
          </div>
        ) : (
          <ul className="divide-y divide-brand-border">
            {d.upcoming_events.map((e) => (
              <li key={e.id} className="flex items-start gap-4 py-3">
                <div className="shrink-0 w-14 text-center bg-brand-navy text-white rounded-md py-2">
                  <div className="text-[10px] uppercase tracking-widest opacity-70">
                    {new Date(e.start_date).toLocaleDateString("fr-FR", { month: "short" })}
                  </div>
                  <div className="font-serif text-xl leading-none">
                    {new Date(e.start_date).getDate()}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-brand-navy truncate">{e.title}</div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                    <span>{frDate(e.start_date)}</span>
                    {e.location && <span className="inline-flex items-center gap-1"><MapPin size={11} />{e.location}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
